// Objects whose sprite is a function of which neighbours are the same thing.
//
// A Chasm is one hole in the ground, not eleven square pits sitting in a row.
// Drawn as eleven identical tiles it reads as tiling rather than as terrain --
// every seam is a straight line the world does not actually contain, and the
// player counts squares instead of seeing a gap. What fixes that is not a
// better sprite: it is **sixteen** sprites, one per combination of which sides
// have more of the same thing beyond them, so a run of them draws one rim
// around the whole shape and none through the middle of it.
//
// This is `sim/cable.ts`'s trick with the electrical part taken out. A cable
// works out its piece from which neighbours it is *joined to*, which for a wire
// is a question about conduction; for anything else the question is only "is
// that the same substance as me", and nothing in the simulation needs to know
// the answer. So the rule here reads one field and nothing else:
//
//   **Two objects are the same substance when their `autotile.key` matches.**
//
// Deliberately an explicit key rather than a comparison of names or textures.
// A name is a claim about behaviour that a rename is allowed to change, and a
// texture is the thing this module *computes* -- keying a drawing rule off
// either would make renaming an object silently redraw the room, which is the
// kind of coupling that is discovered by screenshot a year later.
//
// **Presentation only, and it owes nothing to anything.** No rule reads the
// shape, no cast writes it, and the settle system below adds and removes no
// object. A room with this module deleted would play identically and look
// square.
import { ObjectDef, RoomDef } from './types';
import { SimEvents } from './events';
import { WorldState } from './state';

/** The four directions a piece can join, in the order their letters are
 * concatenated into a texture key. Fixed so that a key is stable: `ns` is
 * always `ns` and never `sn`. */
const AUTOTILE_DIRS: { letter: string; dx: number; dy: number }[] = [
  { letter: 'n', dx: 0, dy: -1 },
  { letter: 'e', dx: 1, dy: 0 },
  { letter: 's', dx: 0, dy: 1 },
  { letter: 'w', dx: -1, dy: 0 },
];

/** Every shape suffix, in mask order -- `none`, `n`, `e`, `ne`, ... `nesw`.
 *
 * The letters name the sides that have **more of the same substance** beyond
 * them, so `nesw` is the middle of a large shape and `none` is a lone tile.
 * Whatever draws these therefore puts an edge on the sides the suffix does
 * *not* name, which is what makes one rim go round the outside of a run.
 *
 * Exported because the renderer generates one texture per entry and the object
 * catalog lists them, and neither should carry its own copy of a list this one
 * can produce. */
export const AUTOTILE_SHAPES: string[] = Array.from({ length: 16 }, (_, mask) => {
  const suffix = AUTOTILE_DIRS.filter((_dir, i) => mask & (1 << i))
    .map((dir) => dir.letter)
    .join('');
  return suffix || 'none';
});

/** Every texture one `autotile.key` can ever wear -- the closed set a renderer
 * has to be able to draw, and the list the catalog shows. */
export function autotileTextures(key: string): string[] {
  return AUTOTILE_SHAPES.map((shape) => `${key}-${shape}`);
}

/** Which shape this object currently reads as -- `none` through `nesw`.
 *
 * A neighbour counts when it carries the same `autotile.key`, and nothing else
 * is asked of it: a Chasm is a Chasm whatever else is standing on the tile, and
 * a rule that also demanded (say) that it be impassable would be a second
 * opinion about what a chasm is, kept in the renderer. */
export function autotileShape(room: RoomDef, obj: ObjectDef): string {
  const key = obj.autotile?.key;
  if (key === undefined) return 'none';
  const suffix = AUTOTILE_DIRS.filter((dir) =>
    room.objects.some((o) => o.id !== obj.id && o.x === obj.x + dir.dx && o.y === obj.y + dir.dy && o.autotile?.key === key),
  )
    .map((dir) => dir.letter)
    .join('');
  return suffix || 'none';
}

/** The texture key for one tile: its family, cut to the piece that matches its
 * neighbours. */
export function autotileTexture(room: RoomDef, obj: ObjectDef): string {
  return `${obj.autotile!.key}-${autotileShape(room, obj)}`;
}

/** The declared system's own body (see settle.ts): re-derives every autotiled
 * object's texture.
 *
 * Only the texture, deliberately -- unlike the ladders in `presentation.ts`,
 * which move a name and a description with the sprite because they are stages
 * of *becoming a different thing*. A chasm with floor to its north and a chasm
 * with floor to its south are the same pit seen from two sides; renaming one
 * would be the world claiming a difference the player would be right to go
 * looking for.
 *
 * Run over the whole room rather than over the tiles that changed, for the same
 * reason `settleCables` and `settleRust` are: a push, a split or a cast can
 * change what any tile's neighbour *is*, and a system that tried to work out
 * which pieces that could have affected would be maintaining a dependency graph
 * to save a loop over one room's objects. */
export function settleAutotiles(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (!obj.autotile) continue;
    const texture = autotileTexture(room, obj);
    if (obj.texture === texture) continue;
    out.push({ kind: 'texture-set', objectId: obj.id, texture, transition: 'instant' });
    obj.texture = texture;
  }
}
